/*
  assets/gcitypartners-source-policy.js — GCITYPARTNERS-SOURCE-POLICY-03
  Owner: GCityPartners public-source quota projection for declared same-origin endpoints.
  Boundary: never sends the client quota identifier to a cross-origin request.
*/
(function (window, document) {
  'use strict';

  var VERSION = '2026-10-02-gcp-source-policy-v4';
  var STORAGE_KEY = 'littleg:public-source-client:v1';
  var TARGET_SELECTOR = '[data-gcp-source-policy]';
  var DEFAULT_LIMIT = 20;
  var DEFAULT_WINDOW = 60;
  var DEFAULT_BURST_LIMIT = 6;
  var DEFAULT_BURST_WINDOW = 10;

  /** @typedef {ReturnType<typeof detailFromResponse>} SourcePolicyDetail */
  /**
   * @type {Window & {
   *   __GCITYPARTNERS_SOURCE_POLICY__?: string,
   *   __gcpSourceSessionId?: string,
   *   __gcpSourcePolicyFetchPatched?: boolean
   * }}
   */
  const policyWindow = window;
  if (policyWindow.__GCITYPARTNERS_SOURCE_POLICY__ === VERSION) return;
  policyWindow.__GCITYPARTNERS_SOURCE_POLICY__ = VERSION;

  /** @param {unknown} value */
  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function isZh() {
    return clean(document.documentElement.lang).toLowerCase().indexOf('zh') === 0;
  }

  function uid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return 'src_' + window.crypto.randomUUID().replace(/-/g, '');
      }
    } catch (_error) {}

    var bytes = new Uint8Array(16);
    try {
      window.crypto.getRandomValues(bytes);
      return (
        'src_' +
        Array.prototype.map
          .call(bytes, function (value) {
            return value.toString(16).padStart(2, '0');
          })
          .join('')
      );
    } catch (_error) {
      return 'src_session_' + Date.now().toString(36);
    }
  }

  function clientId() {
    try {
      var existing = clean(window.localStorage.getItem(STORAGE_KEY));
      if (/^[A-Za-z0-9_-]{12,80}$/.test(existing)) return existing;
      var created = uid();
      window.localStorage.setItem(STORAGE_KEY, created);
      return created;
    } catch (_error) {
      if (!policyWindow.__gcpSourceSessionId) policyWindow.__gcpSourceSessionId = uid();
      return policyWindow.__gcpSourceSessionId;
    }
  }

  /** @param {unknown} input */
  function inputUrl(input) {
    if (typeof input === 'string') return input;
    if (typeof URL === 'function' && input instanceof URL) return input.href;
    if (
      input !== null &&
      typeof input === 'object' &&
      'url' in input &&
      typeof input.url === 'string'
    )
      return input.url;
    return '';
  }

  /** @param {URL | null | undefined} url */
  function managed(url) {
    if (
      !url ||
      (url.origin !== window.location.origin && url.origin !== 'https://api.glhksolution.com')
    )
      return false;

    var path = clean(url.pathname);
    if (url.origin !== window.location.origin && !/^\/apps\/gcity-api\/data\//.test(path))
      return false;
    path = path.replace(/^\/apps\/gcity-api\/(?:core|data)(?=\/)/, '');
    return (
      /^\/api\/(?:weather|programmes)(?:\/|$)/.test(path) ||
      /^\/api\/explore\//.test(path) ||
      /^\/api\/(?:littleg\/lobby-public-context|air-quality\/current)\/?$/.test(path)
    );
  }

  /**
   * @param {Response} response
   * @param {string} name
   * @param {number} fallback
   * @returns {number}
   */
  function numberHeader(response, name, fallback) {
    var value = Number(response.headers.get(name));
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  /** @param {Response} response */
  function detailFromResponse(response) {
    return {
      status: response.status,
      ok: response.ok,
      limited: response.status === 429,
      retryAfter: numberHeader(response, 'Retry-After', 0),
      limit: numberHeader(response, 'X-LittleG-Quota-Limit', DEFAULT_LIMIT),
      windowSeconds: numberHeader(response, 'X-LittleG-Quota-Window', DEFAULT_WINDOW),
      burstLimit: numberHeader(response, 'X-LittleG-Quota-Burst-Limit', DEFAULT_BURST_LIMIT),
      burstWindowSeconds: numberHeader(
        response,
        'X-LittleG-Quota-Burst-Window',
        DEFAULT_BURST_WINDOW
      ),
      enforcement: clean(response.headers.get('X-LittleG-Quota-Enforcement')) || 'configured',
      registration: clean(response.headers.get('X-LittleG-Source-Registration')) || 'not-required',
      apiKey: clean(response.headers.get('X-LittleG-Source-Api-Key')) || 'not-required',
    };
  }

  function targets() {
    return Array.from(document.querySelectorAll(TARGET_SELECTOR));
  }

  /** @param {Partial<SourcePolicyDetail> | null} [detail] */
  function render(detail) {
    /** @type {Partial<SourcePolicyDetail>} */
    const state = detail || {};

    targets().forEach(function (node) {
      node.setAttribute(
        'data-source-policy-state',
        state.limited ? 'rate-limited' : state.ok === false ? 'unavailable' : 'ready'
      );

      if (state.limited) {
        node.textContent = isZh()
          ? '活動／天氣／公共設施官方資料已達免費額度：每裝置每 ' +
            state.windowSeconds +
            ' 秒最多 ' +
            state.limit +
            ' 次；請約 ' +
            (state.retryAfter || state.windowSeconds) +
            ' 秒後再試。這不代表資料不存在。'
          : 'The free quota for official programme, weather and public-facility data has been reached: ' +
            state.limit +
            ' requests per device every ' +
            state.windowSeconds +
            ' seconds. Retry in about ' +
            (state.retryAfter || state.windowSeconds) +
            ' seconds. This does not mean the source has no data.';
        return;
      }

      if (state.ok === false) {
        node.textContent = isZh()
          ? '活動／天氣／公共設施官方來源暫時不可用；這不代表沒有資料。可稍後重試，並以官方頁面為準。'
          : 'The official programme, weather or public-facility source is temporarily unavailable; this does not mean there is no data. Retry later and rely on the official page.';
        return;
      }

      node.textContent = isZh()
        ? '免費官方資料通道（只適用於活動、天氣與公共設施）：香港政府開放資料｜毋須註冊、毋須 API Key｜每裝置每 ' +
          (state.windowSeconds || DEFAULT_WINDOW) +
          ' 秒最多 ' +
          (state.limit || DEFAULT_LIMIT) +
          ' 次；' +
          (state.burstWindowSeconds || DEFAULT_BURST_WINDOW) +
          ' 秒最多 ' +
          (state.burstLimit || DEFAULT_BURST_LIMIT) +
          ' 次｜精確剩餘次數不提供；招標及其他機會按各自來源政策顯示。'
        : 'Free official-data channel (programmes, weather and public facilities only): Hong Kong government open data · no registration or API key · up to ' +
          (state.limit || DEFAULT_LIMIT) +
          ' requests per device every ' +
          (state.windowSeconds || DEFAULT_WINDOW) +
          ' seconds and ' +
          (state.burstLimit || DEFAULT_BURST_LIMIT) +
          ' per ' +
          (state.burstWindowSeconds || DEFAULT_BURST_WINDOW) +
          ' seconds · exact remaining count is not exposed · tenders and other opportunities follow their own source policies.';
    });
  }

  /** @param {SourcePolicyDetail} detail */
  function dispatch(detail) {
    render(detail);
    try {
      document.dispatchEvent(
        new CustomEvent('gcitypartners:source-policy', {
          detail: detail,
        })
      );
    } catch (_error) {}
  }

  function patchFetch() {
    if (policyWindow.__gcpSourcePolicyFetchPatched || typeof window.fetch !== 'function') return;

    policyWindow.__gcpSourcePolicyFetchPatched = true;
    var originalFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      var source = inputUrl(input);
      if (!source) return originalFetch(input, init);

      var url;
      try {
        url = new URL(source, window.location.origin);
      } catch (_error) {
        return originalFetch(input, init);
      }

      if (!managed(url)) return originalFetch(input, init);
      if (!url.searchParams.has('source_client')) {
        url.searchParams.set('source_client', clientId());
      }

      var nextInput =
        typeof Request === 'function' && input instanceof Request
          ? new Request(url.toString(), input)
          : url.toString();

      return originalFetch(nextInput, init).then(function (response) {
        dispatch(detailFromResponse(response));
        return response;
      });
    };
  }

  patchFetch();

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        render({ ok: true });
      },
      { once: true }
    );
  } else {
    render({ ok: true });
  }

  document.addEventListener('shopify:section:load', function () {
    render({ ok: true });
  });
})(window, document);
